import { FormControl, FormLabel, Grid, Typography } from "@mui/material";
import type { Dayjs } from "dayjs";
import { max, sortBy } from "lodash";
import type { FC } from "react";
import type { ProductResponse } from "../../admin/products/productsApi";
import CurrencyPicker from "../../components/currencyPicker/CurrencyPicker";
import NumberField from "../../components/numberField/NumberField";
import type { Currency } from "../../data/BaseEntity";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import { removeArr, updateArr } from "../../misc/utils";
import type { CalculationProduct, CalculationVersion } from "../calculationTypes";
import CustomCalculationProducts from "./CustomCalculationProducts";
import type { PricesByDatesProductsAndOptions } from "./useProductsSellingPrices";
import { useProductsWithPrices } from "./useProductsWithPrices";

export interface Props {
  readonly startDate: Dayjs;
  readonly totalParticipantsNumber: number;
  readonly paidParticipantsNumber: number;
  readonly currency: Currency | undefined;
  readonly margin: number | undefined;
  readonly products: CalculationProduct[];
  readonly availableProducts: ProductResponse[];
  readonly prices: PricesByDatesProductsAndOptions;
  readonly errors: string[];
  readonly isCustomer: boolean;
  readonly onChange: (change: Partial<Pick<CalculationVersion, "products" | "currency" | "margin">>) => void;
  readonly onReplaceProduct: (productId: string, replacement: CalculationProduct) => void;
}

function currencyForProduct(availableProducts: ProductResponse[], productId?: string): Currency | undefined {
  return availableProducts.find((product) => product.id === productId)?.currency;
}

const ProductsEdit: FC<Props> = ({
  startDate,
  currency,
  margin,
  totalParticipantsNumber,
  paidParticipantsNumber,
  products,
  availableProducts,
  prices,
  errors,
  isCustomer,
  onChange,
  onReplaceProduct,
}) => {
  const { productsWithPrices } = useProductsWithPrices(products, currency, margin, availableProducts, prices);

  const handleAddProduct = (product?: { id: string; date: string }) => {
    function nextDate() {
      const lastDate = products.length > 0 ? max(products.map((p) => p.date)) : null;
      const newDate = lastDate ? dayjs(lastDate, ISO_DATE_FORMAT).add(1, "days") : startDate;
      return newDate.format(ISO_DATE_FORMAT);
    }

    function nextDaysShift() {
      const lastDaysShift = products.length > 0 ? max(products.map((p) => p.daysShift)) : null;
      return lastDaysShift ? lastDaysShift + 1 : 0;
    }

    const newProduct = product
      ? { ...product, daysShift: dayjs(product.date, ISO_DATE_FORMAT).diff(startDate, "days"), options: [] }
      : { id: undefined, date: nextDate(), daysShift: nextDaysShift(), options: [] };
    const selectedCurrency = currency ?? currencyForProduct(availableProducts, product?.id);
    onChange({
      currency: selectedCurrency,
      products: sortBy(updateArr(products, products.length, newProduct as CalculationProduct), (prd) => prd.date),
    });
  };

  const handleProductChange = (p: CalculationProduct, idx: number) => {
    const previousProductId = products[idx]?.id;
    const isNewProduct = !previousProductId && p.id;
    const options = isNewProduct ? p.options.map((o) => ({ ...o, quantity: 0 })) : p.options;

    const selectedCurrency =
      currency ?? (p.id !== previousProductId ? currencyForProduct(availableProducts, p.id) : undefined);
    onChange({
      currency: selectedCurrency,
      products: sortBy(updateArr(products, idx, { ...p, options }), (prd) => prd.date),
    });
  };

  const handleProductDelete = (idx: number) => {
    onChange({ products: removeArr(products, idx) });
  };

  const selectedProducts = productsWithPrices.map((p) => ({
    ...p,
    error: errors.includes(p.id),
  }));

  return (
    <>
      <Grid size={{ xs: 4, lg: 2 }}>
        <FormControl>
          <FormLabel>Waluta</FormLabel>
          <CurrencyPicker value={currency ?? null} onChange={(nextCurrency) => onChange({ currency: nextCurrency })} />
        </FormControl>
      </Grid>
      {!isCustomer && (
        <Grid size={{ xs: 3, lg: 2 }}>
          <NumberField
            label="Marża (%)"
            value={margin}
            onChange={(nextMargin) => onChange({ margin: nextMargin ?? undefined })}
          />
        </Grid>
      )}
      <Grid size={12}>
        <CustomCalculationProducts
          selectedProducts={selectedProducts}
          availableProducts={availableProducts}
          calculationCurrency={currency}
          margin={margin}
          totalParticipantsNumber={totalParticipantsNumber}
          paidParticipantsNumber={paidParticipantsNumber}
          onChange={handleProductChange}
          onAdd={handleAddProduct}
          onDelete={handleProductDelete}
          onReplaceProduct={onReplaceProduct}
        />
      </Grid>
      <Grid size={12}>
        {errors.length > 0 && (
          <Typography variant="body2" color="error">
            Nie wszystkie produkty mają ceny w walucie zgodnej z wybraną dla kalkulacji
          </Typography>
        )}
      </Grid>
    </>
  );
};

export default ProductsEdit;
